import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import AuthContainer from "../../containers/Auth";
import { TokenContext } from "../../service/context/tokencontent";
import { validationSchema } from "../../service/validation/otpVerification";
import CustomModal from "../../components/Modal";
import OtpInput from "../../components/OTP";
import { ApiService } from "../../service/api.service";
import { AUTH_URL } from "../../common/common";
import { check, verificationbadge } from "../../common/svg";
import SecureImage from "../../components/BucketImage/Image";
import {
  PhoneAuthProvider,
  RecaptchaVerifier,
  signInWithCredential,
  signInWithPhoneNumber,
} from "firebase/auth";
import { auth } from "../../service/firebase/firebase";
import toast from "react-hot-toast";

// Define interfaces for form data and API responses
const OTPVerification: React.FC = () => {
  const apiService = new ApiService();
  const tokenContext = useContext(TokenContext);
  const navigate = useNavigate();


  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      emailOtp: "",
      mobile: "",
    },
    mode: "onChange",
    resolver: yupResolver(validationSchema),
  });

  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [emailTimer, setEmailTimer] = useState<number>(30);
  const [mobileTimer, setMobileTimer] = useState<number>(30);
  const [emailVerified, setEmailVerified] = useState<boolean>(false);
  const [mobileVerified, setMobileVerified] = useState<boolean>(false);
  const [error, setError] = useState<any>()
  const [resetEmailOtp, setResetEmailOtp] = useState<boolean>(false);
  const [resetMobileOtp, setResetMobileOtp] = useState<boolean>(false);
  const [resendMobile, setResendMobile] = useState<boolean>(false)
  const [isSendingOTP, setIsSendingOTP] = useState(false);

  // Get stored values
  const entityId: string | null = localStorage.getItem("entity_id");
  const emailId: string | null = localStorage.getItem("email");
  const verificationId: string | null = localStorage.getItem(
    "motsop-verficationId"
  );

  // Watch OTP values
  const emailOtp: string = watch("emailOtp");
  const mobileOtp: string = watch("mobile");

  // Handle email timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (emailTimer > 0) {
      timer = setTimeout(() => setEmailTimer(emailTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [emailTimer]);

  // Handle mobile timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (mobileTimer > 0) {
      timer = setTimeout(() => setMobileTimer(mobileTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [mobileTimer]);

  useEffect(() => {
    if (emailVerified && mobileVerified) {
      setModalOpen(true);
    }
  }, [emailVerified, mobileVerified]);
  const closeModal = (): void => {
    setModalOpen(false);
    navigate("/businesstype");
  };

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (isModalOpen) {
      timer = setTimeout(() => {
        closeModal(); // auto-close modal after 5s
      }, 5000);
    }
    return () => clearTimeout(timer); // cleanup if modal closes before 5s
  }, [isModalOpen, closeModal]);

  const resendEmailOTP = async (): Promise<void> => {
    if (!entityId || !emailId) {
      console.error("Entity ID or Email ID is missing");
      return;
    }

    try {
      await apiService.postApi(`${AUTH_URL}/send/otp-route`, {
        entityId,
        emailId,
      });

      setEmailTimer(30);
      setValue("emailOtp", "");
      setResetEmailOtp(prev => !prev);
    } catch (error) {
      console.error("Email OTP Resend Error:", error);
    }
  };

  const resendMobileOTP = async (): Promise<void> => {
    const phoneNumber = localStorage.getItem("phoneNumber");
    if (!phoneNumber) {
      console.error("Phone number is missing");
      return;
    }

    setIsSendingOTP(true); // Start loading
    setResendMobile(true); // Optional, depending on your logic

    try {
      // Clear and re-initialize reCAPTCHA
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
      }

      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: (response: any) => {
            console.log("reCAPTCHA resolved");
          },
          "expired-callback": () => {
            console.warn("reCAPTCHA expired. Please retry.");
          },
        }
      );
      await window.recaptchaVerifier.render();

      const confirmationResult = await signInWithPhoneNumber(
        auth,
        `+91${phoneNumber}`,
        window.recaptchaVerifier
      );

      localStorage.setItem(
        "motsop-verficationId",
        confirmationResult.verificationId
      );

      setMobileTimer(30); // Start countdown
      setValue("mobile", "");
      setResetMobileOtp(prev => !prev);
    } catch (error: any) {
      console.error("Error resending mobile OTP:", error.message);
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    } finally {
      setIsSendingOTP(false); // Stop loading
    }
  };


  const verifyEmailOTP = async (data: { emailOtp: string }): Promise<void> => {
    if (!entityId) {
      console.error("Entity ID is missing");
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        emailOtp: data.emailOtp,
        entityId: entityId,
      };

      const response: any = await apiService.postApi(
        `${AUTH_URL}/verify/otp-route`,
        payload
      );

      if (response?.status === "200") {
        setEmailVerified(true);
        localStorage.setItem("user_data_motsop", JSON.stringify(response.body.data))
      }
    } catch (error) {
      console.error("Email OTP Verification Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const verifyMobileOTP = async (otp: string): Promise<void> => {
    try {
      setIsSubmitting(true);

      const currentVerificationId = localStorage.getItem("motsop-verficationId");

      if (!currentVerificationId || !otp) {
        setError("Verification ID or OTP is missing");
        throw new Error("Verification ID or OTP is missing");
      }

      try {
        const phoneCredential = PhoneAuthProvider.credential(
          currentVerificationId,
          otp
        );
        const res = await signInWithCredential(auth, phoneCredential);
        console.log(res, "res");

        // Set mobile verified if successful
        setMobileVerified(true);
        setError(""); // Clear any previous errors
      } catch (credentialError: any) {
        // Firebase auth error handling
        if (credentialError.code === 'auth/invalid-verification-code') {
          toast.error("MOBILE: Invalid OTP. Please enter the correct code.");
        } else if (credentialError.code === 'auth/code-expired') {
          toast.error("MOBILE: OTP has expired. Please request a new code.");
        } else {
          toast.error(`MOBILE: Verification failed: ${credentialError.message || "Unknown error"}`);
        }
        console.error("MOBILE: Credential Error:", credentialError);
      }
    } catch (error) {
      console.error("Mobile OTP Verification Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle OTP input completion
  const handleEmailOtpComplete = (otp: string): void => {
    setValue("emailOtp", otp);
    verifyEmailOTP({ emailOtp: otp });
  };

  const handleMobileOtpComplete = (otp: string): void => {
    setValue("mobile", otp);
    // Directly verify the OTP that was just entered
    verifyMobileOTP(otp);
  };

  return (
    <AuthContainer>
      <div className="flex w-full rounded-[21.25px] h-full bg-white font-poppins">
        <div className="w-full h-full">
          <div className="mx-auto my-6 px-6 max-w-lg">
            <div className="h-[100px] w-[239px] bg-[#D9D9D9] flex items-center justify-center mb-6">
              logo
            </div>

            <h1 className="font-bold text-2xl leading-tight">
              Welcome aboard 👋
            </h1>
            <p className="text-base text-[#313957] mb-8">
              Let's Get You Started
            </p>

            <div className="space-y-8">
              {/* Mobile Verification Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-center mb-2">
                  <p className="font-semibold text-lg">
                    {mobileVerified ? `Mobile Number Verified` : `Verify your Mobile Number`}
                  </p>
                  {mobileVerified && (
                    <div className="ml-2">
                      <SecureImage fileKey={verificationbadge} alt="Verified" />
                    </div>
                  )}
                </div>

                <OtpInput
                  length={6}
                  onComplete={handleMobileOtpComplete}
                  errors={errors}
                  disabled={isSubmitting || mobileVerified}
                  className="custom-otp-class"
                  key={resetMobileOtp ? "mobile-reset" : "mobile"}
                  value=""
                />
                {/* {error && <p className="text-center text-[14px]">{error}</p>} */}
                <div id="recaptcha-container" />
              </div>

              {/* Email Verification Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-center mb-2">
                  <p className="font-semibold text-lg">{emailVerified ? `Email ID Verified` : `Verify your Email ID`}</p>
                  {emailVerified && (
                    <div className="ml-2">
                      <SecureImage fileKey={verificationbadge} alt="Verified" />
                    </div>
                  )}
                </div>

                <OtpInput
                  length={6}
                  onComplete={handleEmailOtpComplete}
                  errors={errors}
                  disabled={isSubmitting || emailVerified}
                  className="custom-otp-class"
                  key={resetEmailOtp ? "email-reset" : "email"}
                  value=""
                />

                <button
                  type="button"
                  onClick={resendEmailOTP}
                  disabled={emailTimer > 0 || emailVerified}
                  className={`
                    w-full h-12 px-4 py-2 
                    rounded-2xl text-white text-base font-medium 
                    bg-[#444FBD] 
                    transition duration-300 
                    ${emailTimer > 0 || emailVerified
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-[#3a45a0]"
                    }
                  `}
                >
                  {emailVerified ? (
                    "Email Verified Successfully"
                  ) : emailTimer > 0 ? (
                    <>
                      <span className="mr-2">
                        Resend Email OTP in {emailTimer}s
                      </span>
                      <svg
                        className="inline w-4 h-4 animate-spin text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        ></path>
                      </svg>
                    </>
                  ) : (
                    "Resend Email OTP"
                  )}
                </button>
                <button
                  type="button"
                  onClick={resendMobileOTP}
                  disabled={mobileTimer > 0 || mobileVerified || isSendingOTP}
                  className={`
    w-full h-12 px-4 py-2 
    rounded-2xl text-white text-base font-medium 
    bg-[#444FBD] 
    transition duration-300 
    ${mobileTimer > 0 || mobileVerified || isSendingOTP
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-[#3a45a0]"}
  `}
                >
                  {mobileVerified ? (
                    "Mobile Verified Successfully"
                  ) : isSendingOTP ? (
                    <>
                      <span className="mr-2">Sending OTP...</span>
                      <svg
                        className="inline w-4 h-4 animate-spin text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        ></path>
                      </svg>
                    </>
                  ) : mobileTimer > 0 ? (
                    <>
                      <span className="mr-2">Resend Mobile OTP in {mobileTimer}s</span>
                      <svg
                        className="inline w-4 h-4 animate-spin text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        ></path>
                      </svg>
                    </>
                  ) : (
                    "Resend Mobile OTP"
                  )}
                </button>

              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        isOpen={isModalOpen}
        content={
          <div className="flex flex-col items-center p-6">
            <SecureImage
              fileKey={check}
              alt="check"
              className="mb-4 w-16 h-16"
            />
            <h3 className="text-xl font-semibold text-[#4B4B4B] mb-2">
              Verification Successful
            </h3>
            <p className="text-base text-[#4B4B4B] mb-6 text-center">
              Your account has been created successfully
            </p>
            <button
              onClick={closeModal}
              className="bg-[#444FBD] rounded-md py-2 px-6 text-white font-medium hover:bg-[#3a45a0] transition duration-300"
            >
              Continue
            </button>
          </div>
        }
        onClose={closeModal}
      />
    </AuthContainer>
  );
};

export default OTPVerification;